import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { GuardianMetricsCard } from './GuardianMetricsCard';
import { ChakraBalanceChart } from './ChakraBalanceChart';
import { AuraScanHistory } from './AuraScanHistory';
import { GuardianLevelBadge } from './GuardianLevelBadge';
import { TemporalAlignmentChart } from './TemporalAlignmentChart';

export function GuardianDashboard() {
  const [activeModule, setActiveModule] = useState<string | null>(null);

  const guardianData = {
    level: 7,
    title: 'Nexus Guardian',
    experience: 2450,
    nextLevelExp: 3000,
    auraPurity: 92,
    heartPurity: 88,
    temporalAlignment: 85,
    cosmicResonance: 90
  };

  const modules = [
    { id: 'schumann', name: 'Schumann Resonance', minLevel: 1, icon: '🌍' },
    { id: 'quantum', name: 'Quantum Fields', minLevel: 3, icon: '⚛️' },
    { id: 'harmonic', name: 'Harmonic Nexus', minLevel: 5, icon: '🎵' },
    { id: 'temporal', name: 'Temporal Matrix', minLevel: 7, icon: '⏰' },
    { id: 'cosmic', name: 'Cosmic Oracle', minLevel: 10, icon: '🌌' }
  ];

  const chakraIcons = [
    'https://d64gsuwffb70l.cloudfront.net/68b55da3cc8df3f651647221_1757947007748_269a2af9.webp',
    'https://d64gsuwffb70l.cloudfront.net/68b55da3cc8df3f651647221_1757947009477_da8009dc.webp',
    'https://d64gsuwffb70l.cloudfront.net/68b55da3cc8df3f651647221_1757947011222_e4552c3b.webp',
    'https://d64gsuwffb70l.cloudfront.net/68b55da3cc8df3f651647221_1757947012974_574f7d39.webp'
  ];

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-6"
      style={{
        backgroundImage: `url('https://d64gsuwffb70l.cloudfront.net/68b55da3cc8df3f651647221_1757947002667_0a435c5d.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'overlay'
      }}
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Guardian Dashboard</h1>
          <p className="text-purple-300">Welcome to your spiritual command center</p>
        </div>

        {/* Guardian Level Badge */}
        <GuardianLevelBadge
          level={guardianData.level}
          title={guardianData.title}
          experience={guardianData.experience}
          nextLevelExp={guardianData.nextLevelExp}
        />

        <Tabs defaultValue="metrics" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-purple-900/30 border-purple-500/30">
            <TabsTrigger value="metrics" className="text-purple-200">Metrics</TabsTrigger>
            <TabsTrigger value="history" className="text-purple-200">History</TabsTrigger>
            <TabsTrigger value="modules" className="text-purple-200">Modules</TabsTrigger>
            <TabsTrigger value="alignment" className="text-purple-200">Alignment</TabsTrigger>
          </TabsList>

          <TabsContent value="metrics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <GuardianMetricsCard
                title="Aura Purity"
                value={guardianData.auraPurity}
                maxValue={100}
                level="Pure"
                trend="up"
                icon={chakraIcons[0]}
              />
              <GuardianMetricsCard
                title="Heart Purity"
                value={guardianData.heartPurity}
                maxValue={100}
                level="Balanced"
                trend="stable"
                icon={chakraIcons[1]}
              />
              <GuardianMetricsCard
                title="Temporal Alignment"
                value={guardianData.temporalAlignment}
                maxValue={100}
                level="Aligned"
                trend="up"
                icon={chakraIcons[2]}
              />
              <GuardianMetricsCard
                title="Cosmic Resonance"
                value={guardianData.cosmicResonance}
                maxValue={100}
                level="Resonant"
                trend="up"
                icon={chakraIcons[3]}
              />
            </div>
            <ChakraBalanceChart />
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AuraScanHistory />
              <Card className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-500/30">
                <CardHeader>
                  <CardTitle className="text-purple-200">Recent Achievements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-2 rounded bg-purple-900/20">
                      <span className="text-2xl">🏆</span>
                      <div>
                        <p className="text-white font-medium">Temporal Master</p>
                        <p className="text-xs text-purple-400">Achieved perfect temporal alignment</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 p-2 rounded bg-purple-900/20">
                      <span className="text-2xl">⭐</span>
                      <div>
                        <p className="text-white font-medium">Chakra Harmonizer</p>
                        <p className="text-xs text-purple-400">Balanced all energy centers</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="modules" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {modules.map((module) => (
                <Card 
                  key={module.id}
                  className={`bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-500/30 transition-all duration-300 ${
                    guardianData.level >= module.minLevel 
                      ? 'hover:border-purple-400/50 cursor-pointer' 
                      : 'opacity-50'
                  }`}
                  onClick={() => guardianData.level >= module.minLevel && setActiveModule(module.id)}
                >
                  <CardContent className="p-6 text-center">
                    <div className="text-4xl mb-3">{module.icon}</div>
                    <h3 className="text-lg font-bold text-white mb-2">{module.name}</h3>
                    <p className="text-sm text-purple-300 mb-4">
                      Required Level: {module.minLevel}
                    </p>
                    <Button 
                      disabled={guardianData.level < module.minLevel}
                      className="w-full bg-purple-600 hover:bg-purple-500"
                    >
                      {guardianData.level >= module.minLevel ? 'Access' : 'Locked'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="alignment" className="space-y-6">
            <TemporalAlignmentChart />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}